<template>
  <SCASelect
    :model-value="model"
    :items="items"
    :icon="currentIcon"
    use-smaller-vertical-padding
    item-icon-size="16px"
    no-mobile-margin
    :class="$style.root"
    test-id="column-filter-operator"
    @update:model-value="onSelect"
  >
    <template v-if="removable" #after>
      <ConditionRemoveButton :class="$style.remove" @remove="emit('remove')" />
    </template>
  </SCASelect>
</template>

<script setup lang="ts">
import { computed } from "vue";

import SCASelect from "../../SCASelect.vue";
import type { FilterOperator } from "../filterModel";
import type { OperatorOption } from "../types";
import ConditionRemoveButton from "./ConditionRemoveButton.vue";
import { OPERATOR_SPECS, operatorLabel } from "./operators";

const props = defineProps<{
  options: readonly OperatorOption[];
  removable?: boolean;
}>();

const emit = defineEmits<{
  remove: [];
}>();

const model = defineModel<FilterOperator>({ required: true });

// An operator outside the menu (a money column's Contains) stays visible until the user picks another.
const items = computed(() => {
  const listed = props.options.map((option) => ({
    id: option.value,
    label: option.label,
    icon: option.icon,
    disabled: option.disabled,
  }));
  if (listed.some((item) => item.id === model.value)) return listed;
  return [{ id: model.value, label: operatorLabel(model.value), icon: OPERATOR_SPECS[model.value].icon }, ...listed];
});

const currentIcon = computed(() => items.value.find((item) => item.id === model.value)?.icon);

function onSelect(value: string | number) {
  model.value = value as FilterOperator;
}
</script>

<style module lang="scss">
@use "../../../styles/icon.scss" as IconMixins;

.root svg {
  width: 16px;
  height: 16px;
}

.root svg:has(~ input),
.root [popover] svg {
  @include IconMixins.icon-color(var(--color-content-disabled));
}

.root input {
  user-select: none;

  &::selection {
    background: transparent;
  }
}

.root .remove {
  margin-left: calc(2px - var(--sca-input-base-gap, var(--space-component-padding-small)));

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
